import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormHelperText from '@mui/material/FormHelperText';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Control, useFieldArray, Controller } from 'react-hook-form';

import { DAYS_LIST } from '../schemas';

type Props = {
  control: Control<any>;
};

const AvailabilityRules = ({ control }: Props) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'availabilityRules',
  });

  const capitalizeDay = (day: string) => day.charAt(0).toUpperCase() + day.slice(1);

  return (
    <Box>
      <Box sx={styles.header}>
        <Typography sx={styles.hint}>
          Leave empty to allow coupon any time. Rules restrict usage to specific days/hours.
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => append({ days: [], from: '09:00', to: '21:00' })}
          sx={styles.addButton}
        >
          Add Rule
        </Button>
      </Box>

      {fields.map((field, index) => (
        <Box key={field.id} sx={styles.ruleRow}>
          <Box sx={styles.ruleContent}>
            <Box sx={styles.daysSection}>
              <Typography sx={styles.daysLabel}>Days</Typography>
              <Controller
                control={control}
                name={`availabilityRules.${index}.days`}
                render={({ field: f, fieldState: { error } }) => (
                  <Box>
                    <Box sx={styles.daysCheckboxGroup}>
                      {DAYS_LIST.map((day) => (
                        <FormControlLabel
                          key={day}
                          control={
                            <Checkbox
                              size="small"
                              checked={f.value?.includes(day) || false}
                              onChange={(e) => {
                                const currentDays = f.value || [];
                                if (e.target.checked) {
                                  f.onChange([...currentDays, day]);
                                } else {
                                  f.onChange(currentDays.filter((d: string) => d !== day));
                                }
                              }}
                            />
                          }
                          label={<Typography sx={styles.dayCheckboxLabel}>{capitalizeDay(day)}</Typography>}
                        />
                      ))}
                    </Box>
                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                  </Box>
                )}
              />
            </Box>

            <Box sx={styles.timeSection}>
              <Controller
                control={control}
                name={`availabilityRules.${index}.from`}
                render={({ field: f, fieldState: { error } }) => (
                  <TextField
                    {...f}
                    size="small"
                    label="Start Time"
                    type="time"
                    sx={styles.timeField}
                    error={!!error}
                    helperText={error?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />

              <Controller
                control={control}
                name={`availabilityRules.${index}.to`}
                render={({ field: f, fieldState: { error } }) => (
                  <TextField
                    {...f}
                    size="small"
                    label="End Time"
                    type="time"
                    sx={styles.timeField}
                    error={!!error}
                    helperText={error?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Box>
          </Box>

          <IconButton
            size="small"
            onClick={() => remove(index)}
            color="error"
            sx={styles.deleteButton}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

export default AvailabilityRules;

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 2,
    gap: 2,
  },
  hint: {
    fontFamily: 'GilroyRegular',
    fontSize: 12,
    color: '#888',
    fontStyle: 'italic',
  },
  addButton: {
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    textTransform: 'none' as const,
    color: '#1a1a1a',
    flexShrink: 0,
    border: '1px dashed #ccc',
  },
  ruleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
    mb: 2,
    p: 2,
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
  },
  ruleContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  daysSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 1,
  },
  daysLabel: {
    fontFamily: 'GilroySemiBold',
    fontSize: 13,
    color: '#555',
  },
  daysCheckboxGroup: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 0.5,
  },
  dayCheckboxLabel: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
  },
  timeSection: {
    display: 'flex',
    gap: 2,
    alignItems: 'center',
  },
  timeField: {
    width: 180,
  },
  deleteButton: {
    mt: 0.5,
  },
};
